export const PRIMARY_GUIDES = ['getting-started', 'authentication', 'use-cases', 'organizations-workspaces', 'api-reference', 'plans-limits', 'changelog'];
export const OVERVIEW_CONTENT = {
  title: 'Developer resources',
  intro: 'Integrate delivery evidence into your workflow.',
  description: 'Read delivery intelligence and automate clients, imports, commercial inputs, investigations and frozen reports.',
  access: 'Every request respects your key’s scopes, current access and effective plan.',
  useCases: 'Bring delivery evidence into the tools your agency, consultancy or in-house development team uses every day.',
  planIntro: 'Endpoint access and usage limits are separate. These published allowances help you assess fit; your workspace’s current effective entitlements apply to each request.',
};
export const guidePath = (id) => PRIMARY_GUIDES.includes(id) ? `/docs/${id}` : `/docs/guides/${id}`;
// Describe the actual published guide without duplicating immutable API content.
export function docsDescription(guide) {
  if (!guide) return OVERVIEW_CONTENT.description;
  const paragraph = guide.markdown.split(/\n\s*\n/).find((part) => part.trim() && !/^[#|>`\-*]/.test(part.trim()));
  const prose = (paragraph || guide.title).replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').replace(/[`*_]/g, '').replace(/\s+/g, ' ').trim();
  const text = `${guide.title}: ${prose}`;
  return text.length <= 180 ? text : text.slice(0, 177).replace(/\s+\S*$/, '') + '…';
}
export function guideHref(href, guides) {
  const match = /^(?:\.\/)?([a-z][a-z0-9-]*)\.md(?:#([a-z0-9-]+))?$/.exec(href || '');
  return match && guides.some((guide) => guide.id === match[1]) ? guidePath(match[1]) + (match[2] ? `#${match[2]}` : '') : href;
}
export const operationAnchor = (operation) => `#reference/tag/${operation.tag}/operation/${operation.id}`;
export function docsRoutes(guides) { return ['/docs', ...guides.map((guide) => guidePath(guide.id))]; }
export function resolveDocsRoute(path, guides) {
  const clean = path.replace(/\/$/, '');
  if (clean === '/docs') return { type: 'landing' };
  const guide = guides.find((item) => guidePath(item.id) === clean);
  return guide ? { type: guide.id === 'api-reference' ? 'reference' : 'guide', guide } : { type: 'missing' };
}
export function publicSearchIndex(bundle) {
  const plainText = (markdown) => markdown.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').replace(/^[#>]+\s*/gm, '').replace(/[`*]/g, '').replace(/\s+/g, ' ').trim();
  const sections = (guide) => {
    const headings = [...guide.markdown.matchAll(/^## (.+)$/gm)];
    return headings.map((heading, index) => {
      const body = guide.markdown.slice(heading.index, headings[index + 1]?.index ?? guide.markdown.length);
      const title = heading[1].replace(/`/g, '');
      return { title, body: plainText(body), href: guidePath(guide.id) + '#' + title.toLowerCase().replace(/[^a-z0-9 -]/g, '').trim().replace(/\s+/g, '-'), operationId: /\nOperation: `([^`]+)`/.exec(body)?.[1] };
    });
  };
  const guideSections = new Map(bundle.guides.map((guide) => [guide.id, sections(guide)]));
  const operationSections = new Map([...guideSections.values()].flat().filter((section) => section.operationId).map((section) => [section.operationId, section]));
  const overviewBody = [
    ...Object.values(OVERVIEW_CONTENT),
    'Build an integration. Create a scoped, expiring API key. Keep it on your server in an environment variable. Find your organization and workspace. Use verified routing; discover project and client IDs. Choose a documented operation. Review the schema, units, scopes and plan requirements.',
    'Evaluate ScopeWorth. Check plan eligibility and limits, the endpoint catalogue and security controls before choosing a plan. Your first API request. Discover the clients and projects available to your workspace. Run examples on your server. Store your API key in an environment variable; keep it out of browser code. Make your first request.',
    'Use Cases for the API. Prepare recurring client reports. Review scope changes before a client call. Support invoice reviews with evidence. Connect project setup and commercial inputs. Bring delivery evidence to internal investment reviews. Check data readiness before a delivery handoff. Explore use cases and API examples.',
    'Compare the integration essentials. Clients. Projects. Evidence window. Saved reports / month. Sync eligibility. Read all plan limits and caveats. Follow a complete workflow.',
    ...bundle.plans.plans.map((plan) => `${plan.id} ${Object.values(plan.caps).join(' ')}`),
    ...bundle.guides.map((guide) => guide.title),
    ...Object.values(bundle.snippets.find((snippet) => snippet.operationId === 'directory.read')),
    'Examples use synthetic records and a key placeholder. Documentation does not send business API requests. Available evidence and plan limits remain explicit.',
  ].join(' ');
  return [
    { id: 'overview', kind: 'guide', title: OVERVIEW_CONTENT.title, href: '/docs', body: overviewBody, text: overviewBody.toLowerCase() },
    ...bundle.guides.map((guide) => {
      const body = plainText(guide.markdown);
      return { id: guide.id, kind: 'guide', title: guide.title, href: guidePath(guide.id), body, sections: guideSections.get(guide.id), text: `${guide.title} ${body}`.toLowerCase() };
    }),
    ...bundle.navigation.map((op) => {
      const body = operationSections.get(op.id)?.body || op.description;
      return { ...op, kind: 'operation', title: `${op.method} ${op.title}`, href: `/docs/api-reference${operationAnchor(op)}`, body, text: `${op.id} ${op.title} ${op.method} ${op.path} ${op.scope} ${body}`.toLowerCase() };
    }),
  ];
}
export function searchDocs(index, query, limit = 12) {
  const words = query.trim().toLowerCase().slice(0, 160).split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  return index.filter((entry) => words.every((word) => entry.text.includes(word))).map((entry) => {
    const section = entry.sections?.find((section) => words.every((word) => section.body.toLowerCase().includes(word)));
    const body = section?.body || entry.body;
    const firstMatch = Math.min(...words.map((word) => body.toLowerCase().indexOf(word)).filter((position) => position >= 0));
    const start = Number.isFinite(firstMatch) ? Math.max(0, firstMatch - 70) : 0;
    return { ...entry, href: section?.href || entry.href, sectionTitle: section?.title, excerpt: (start ? '…' : '') + body.slice(start, start + 280) + (body.length > start + 280 ? '…' : ''), score: words.reduce((score, word) => score + (entry.title.toLowerCase().includes(word) ? 4 : 0), 0) + (entry.id.toLowerCase() === query.trim().toLowerCase() ? 10 : 0) + (body.toLowerCase().includes(query.trim().toLowerCase()) ? 6 : 0) };
  }).sort((a, b) => b.score - a.score).slice(0, limit);
}
export const SCALAR_SAFE_CONFIG = Object.freeze({
  hideTestRequestButton: true, hideClientButton: true, persistAuth: false,
  telemetry: false, agent: { disabled: true }, withDefaultFonts: false,
  isEditable: false, showDeveloperTools: 'never',
  documentDownloadType: 'none', hideSearch: true, hiddenClients: true, showSidebar: false,
  pathRouting: { basePath: '#reference' }, mcp: { disabled: true },
  theme: 'none', darkMode: false, hideDarkModeToggle: true,
});

// Supported Scalar extensions carry the same tested server-side snippets as guides.
export function referenceView(specification, snippets, plan = 'all') {
  const spec = structuredClone(specification);
  for (const [path, methods] of Object.entries(spec.paths)) {
    for (const [method, operation] of Object.entries(methods)) {
      if (!operation?.operationId) continue;
      if (plan !== 'all' && !operation['x-scopeworth'].plans.includes(plan)) { delete methods[method]; continue; }
      const snippet = snippets.find((item) => item.operationId === operation.operationId);
      if (!snippet) throw new Error('Missing generated operation example');
      operation['x-scalar-examples'] = [
        { lang: 'shell', label: 'cURL', source: snippet.curl },
        { lang: 'javascript', label: 'JavaScript', source: snippet.javascript },
        { lang: 'python', label: 'Python', source: snippet.python },
      ];
      operation['x-scopeworth-operation'] = operation.operationId;
    }
    if (!Object.values(methods).some((operation) => operation?.operationId)) delete spec.paths[path];
  }
  return spec;
}
